import { getLocalizedText } from "@salon/types";
import { FileText } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useServiceTemplates } from "@/domains/settings/features/service-templates";
import { useSalonStore } from "@/lib/store/salon";

interface TemplateSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (templateId: string) => void;
  applying?: boolean;
}

export function TemplateSelectorDialog({
  open,
  onOpenChange,
  onSelectTemplate,
  applying,
}: TemplateSelectorDialogProps) {
  const { t, i18n } = useTranslation("services");
  const _currency = useSalonStore((s) => s.currentSalon?.currency ?? "USD");
  const { systemTemplates, customTemplates, loading, refetch } = useServiceTemplates();

  // Refetch templates when dialog opens
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("templateSelector.title")}</DialogTitle>
          <DialogDescription>{t("templateSelector.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* System Templates */}
          {systemTemplates.length > 0 && (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium">{t("templateSelector.sections.system")}</h4>
                <p className="text-xs text-muted-foreground">
                  {t("templateSelector.sections.systemDescription")}
                </p>
              </div>
              <div className="space-y-2">
                {loading ? (
                  <>
                    <Skeleton className="h-24" />
                    <Skeleton className="h-24" />
                  </>
                ) : (
                  systemTemplates.map((template) => {
                    const templateName =
                      typeof template.name === "string"
                        ? template.name
                        : getLocalizedText(template.name as Record<string, string>, i18n.language);
                    return (
                      <button
                        type="button"
                        key={template.id}
                        onClick={() => onSelectTemplate(template.id)}
                        disabled={applying}
                        className="w-full text-left rounded-lg border p-4 hover:bg-accent hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2">
                                <h5 className="font-medium">{templateName}</h5>
                                <Badge variant="secondary" className="text-xs">
                                  {t("templateSelector.labels.system")}
                                </Badge>
                              </div>
                            </div>
                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {template.groups?.flatMap((group) =>
                              group.options?.map((option) => {
                                const optionName =
                                  typeof option.optionName === "string"
                                    ? option.optionName
                                    : getLocalizedText(
                                        option.optionName as Record<string, string>,
                                        i18n.language
                                      );

                                return (
                                  <div
                                    key={option.id}
                                    className="flex items-center gap-2 text-xs bg-muted px-2 py-1 rounded"
                                  >
                                    <span>{optionName}</span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Custom Templates */}
          {customTemplates.length > 0 && (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium">{t("templateSelector.sections.custom")}</h4>
                <p className="text-xs text-muted-foreground">
                  {t("templateSelector.sections.customDescription")}
                </p>
              </div>
              <div className="space-y-2">
                {loading ? (
                  <Skeleton className="h-24" />
                ) : (
                  customTemplates.map((template) => {
                    const templateName =
                      typeof template.name === "string"
                        ? template.name
                        : getLocalizedText(template.name as Record<string, string>, i18n.language);
                    return (
                      <button
                        type="button"
                        key={template.id}
                        onClick={() => onSelectTemplate(template.id)}
                        disabled={applying}
                        className="w-full text-left rounded-lg border p-4 hover:bg-accent hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1">
                              <h5 className="font-medium">{templateName}</h5>
                            </div>
                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {template.groups?.flatMap((group) =>
                              group.options?.map((option) => {
                                const optionName =
                                  typeof option.optionName === "string"
                                    ? option.optionName
                                    : getLocalizedText(
                                        option.optionName as Record<string, string>,
                                        i18n.language
                                      );

                                return (
                                  <div
                                    key={option.id}
                                    className="flex items-center gap-2 text-xs bg-muted px-2 py-1 rounded"
                                  >
                                    <span>{optionName}</span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {!loading && systemTemplates.length === 0 && customTemplates.length === 0 && (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                {t("templateSelector.messages.noTemplates")}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
